import { c as _c } from "react/compiler-runtime";
import { useQuery, type QueryClient, type UseQueryResult } from "@tanstack/react-query";
import { useAuth } from "@clerk/nextjs";
import { useUserSettings } from "@sukha/query";
import { getActivePersonality, getAllFocusMessages, type MemberFocusMessagesResponse } from "@/hooks/assistant/assistantApi";
import { speakLocaleForMemberCue } from "@/hooks/focusMessages/speakLocale";
import { clearAssistantAudioCache } from "@/hooks/useAssistantAudio";
import { normalizeUiLocale, type MemberUiLocale } from "@sukha/shared/locale/messageLocale";
import { DEFAULT_MEMBER_UI_LOCALE } from "@sukha/shared/locale/memberUiLocales";
export type { MemberFocusMessagesResponse };

/**
 * Member-facing focus message cache key (scoped by assistant personality + UI locale).
 *
 * There is ONE entry, holding every type. It used to be one per type, which is
 * what made a first load fire sixteen requests: thirteen hooks, each with its
 * own key, each fetching alone. The type is now a `select` over this one entry,
 * so every caller shares a single request.
 */
export function focusMessagesQueryKey(personalityId?: string | null, locale?: MemberUiLocale | null): readonly ["focusMessages", string, string] {
  return ["focusMessages", personalityId ?? "unknown", locale ?? "default"] as const;
}

/** Refetch spoken lines and drop stale preloaded audio (locale, personality, admin edits). */
export function refreshMemberFocusMessages(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({
    queryKey: ["focusMessages"]
  });
  clearAssistantAudioCache();
}

/** Current member UI locale from settings (optimistic-aware). */
export function useMemberUiLocale() {
  const $ = _c(2);
  const {
    data: userSettings
  } = useUserSettings();
  const t0 = userSettings?.locale;
  let t1;
  if ($[0] !== t0) {
    t1 = normalizeUiLocale(t0);
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}

/**
 * Active assistant personality for the signed-in member, and whether that
 * question has been answered yet.
 *
 * `isResolved` matters because the personality id is part of the focus-message
 * cache key. Fetching before it is known sends one request under "unknown" and
 * a second under the real id, and the first is thrown away — a wasted API call
 * on the busiest moment of a page load. It is true once the lookup SETTLES,
 * error included: a personality is optional and the API answers without one.
 */
export function useActiveAssistantPersonality() {
  const $ = _c(6);
  const {
    getToken
  } = useAuth();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = ["user-selected-personality"];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== getToken) {
    t1 = {
      queryKey: t0,
      queryFn: async () => {
        const token = (await getToken().catch(_temp)) ?? undefined;
        return getActivePersonality(token);
      },
      staleTime: 600000,
      gcTime: 600000
    };
    $[1] = getToken;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    data,
    isPending
  } = useQuery(t1);
  const t2 = data?.assistantPersonality?.assistantPersonality ?? undefined;
  const t3 = !isPending;
  let t4;
  if ($[3] !== t2 || $[4] !== t3) {
    t4 = {
      personalityId: t2,
      isResolved: t3
    };
    $[3] = t2;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  return t4;
}

/**
 * Active assistant personality id for the signed-in member (from settings or default).
 */
function _temp() {}
export function useActiveAssistantPersonalityId() {
  return useActiveAssistantPersonality().personalityId;
}
const EMPTY_POOL: MemberFocusMessagesResponse = {
  messages: [],
  requestedLocale: DEFAULT_MEMBER_UI_LOCALE,
  resolvedLocale: DEFAULT_MEMBER_UI_LOCALE
};

/**
 * Personality-aware focus messages for a single type (session start, nudges, etc.).
 *
 * Every caller shares one query and one request; `select` narrows it to the type
 * asked for, so a component still only re-renders when its own pool changes.
 */
export function useFocusMessages(type) {
  const $ = _c(13);
  const {
    getToken
  } = useAuth();
  const {
    personalityId,
    isResolved
  } = useActiveAssistantPersonality();
  const memberLocale = useMemberUiLocale();
  let t0;
  if ($[0] !== memberLocale || $[1] !== personalityId) {
    t0 = focusMessagesQueryKey(personalityId, memberLocale);
    $[0] = memberLocale;
    $[1] = personalityId;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] !== getToken || $[4] !== memberLocale) {
    t1 = async () => {
      const token = (await getToken().catch(_temp2)) ?? undefined;
      return getAllFocusMessages(token, memberLocale);
    };
    $[3] = getToken;
    $[4] = memberLocale;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== type) {
    t2 = byType => byType[type] ?? EMPTY_POOL;
    $[6] = type;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== isResolved || $[9] !== t0 || $[10] !== t1 || $[11] !== t2) {
    t3 = {
      queryKey: t0,
      queryFn: t1,
      select: t2,
      enabled: isResolved,
      staleTime: 120000,
      gcTime: 120000,
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
      refetchOnMount: true
    };
    $[8] = isResolved;
    $[9] = t0;
    $[10] = t1;
    $[11] = t2;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  return useQuery(t3);
}

/** TTS locale for a focus message pool (member locale, with English fallback lines). */
function _temp2() {}
export function useFocusMessageSpeakLocale(type) {
  const $ = _c(3);
  const memberLocale = useMemberUiLocale();
  const {
    data
  } = useFocusMessages(type);
  const t0 = data?.resolvedLocale;
  let t1;
  if ($[0] !== memberLocale || $[1] !== t0) {
    t1 = speakLocaleForMemberCue(memberLocale, t0);
    $[0] = memberLocale;
    $[1] = t0;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}

/** Convenience helper for hooks that only need message lines. */
export function useFocusMessageLines(type) {
  const $ = _c(2);
  const {
    data
  } = useFocusMessages(type);
  let t0;
  if ($[0] !== data?.messages) {
    t0 = data?.messages ?? [];
    $[0] = data?.messages;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}